import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Lightbulb, Loader2, Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = { new: "#F5A623", contacted: "#002EA6", qualified: "#8b5cf6", won: "#10B981", archived: "#71717A" };
const TABS = ["all", "new", "contacted", "qualified", "won"];
const STATUSES = ["new", "contacted", "qualified", "won", "archived"];

export default function AdminAdvisory() {
  const [tab, setTab] = useState("all");
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);

  const load = () => {
    setItems(null);
    api.get("/advisory-inquiries").then(({ data }) => setItems(data));
  };
  useEffect(() => { load(); }, []);

  const filtered = (items || []).filter((i) => tab === "all" || (i.status || "new") === tab);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/advisory-inquiries/${id}`, { status });
      setActive(data); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the inquiry from ${active.organization || "this organization"}? This cannot be undone.`)) return;
    try {
      await api.delete(`/advisory-inquiries/${active.id}`);
      toast.success("Inquiry deleted");
      setActive(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const Field = ({ label, value }) => (
    <div><p className="overline text-[0.55rem]">{label}</p><p className="text-sm mt-0.5">{value || "—"}</p></div>
  );

  return (
    <div>
      <PageHeader overline="Inbound advisory requests" title="Advisory Inquiries" />
      <div className="px-8 border-b border-black/10 flex gap-6 bg-white sticky top-16 z-10">
        {TABS.map((t) => (
          <button key={t} data-testid={`advtab-${t}`} onClick={() => setTab(t)}
            className={`py-3 text-sm font-medium border-b-2 -mb-px capitalize transition-colors ${tab === t ? "border-primary text-[#09090B]" : "border-transparent text-muted-foreground hover:text-[#09090B]"}`}>
            {t}
          </button>
        ))}
      </div>
      <div className="p-8">
        {!items && <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
        {items && filtered.length === 0 && <Empty icon={Lightbulb} title="No inquiries here" hint="Submissions from the Advisory Services page land in this inbox." />}
        {items && filtered.length > 0 && (
          <div className="space-y-px bg-black/10 border border-black/10">
            {filtered.map((l) => (
              <button key={l.id} onClick={() => setActive(l)} data-testid={`adv-${l.id}`}
                className="w-full bg-white p-5 flex items-center justify-between hover:bg-[#F1F1F3] transition-colors text-left">
                <div>
                  <p className="font-medium">{l.organization} <span className="text-muted-foreground font-normal">· {l.full_name}</span></p>
                  <p className="overline text-[0.55rem] mt-1">{(l.services || []).join(", ") || "—"}{(l.program_stages || []).length ? ` · ${(l.program_stages || []).join(", ")}` : ""}</p>
                </div>
                <span className="flex items-center gap-1.5 text-xs capitalize"><span className="h-1.5 w-1.5 rounded-full" style={{ background: STATUS_COLOR[l.status] || "#71717A" }} />{l.status || "new"}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-white border-black/10 rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{active.organization}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Contact" value={active.full_name} />
                  <Field label="Role" value={active.role} />
                  <div className="col-span-2"><p className="overline text-[0.55rem]">Email</p><a href={`mailto:${active.email}`} className="text-sm text-primary hover:underline flex items-center gap-1 mt-0.5"><Mail className="h-3 w-3" />{active.email}</a></div>
                  <div className="col-span-2"><Field label="Program stage" value={(active.program_stages || []).join(", ")} /></div>
                  <div className="col-span-2"><Field label="Services needed" value={(active.services || []).join(", ")} /></div>
                </div>
                {active.message && <div className="border border-black/10 bg-[#F4F4F5] p-3"><p className="overline text-[0.55rem] mb-1">Message</p><p className="text-sm">{active.message}</p></div>}
                <div className="flex items-end gap-3 pt-2 border-t border-black/10">
                  <div className="flex-1">
                    <p className="overline text-[0.55rem] mb-1">Status</p>
                    <Select value={active.status || "new"} onValueChange={(v) => setStatus(active.id, v)}>
                      <SelectTrigger data-testid="adv-status" className="bg-[#F4F4F5] border-black/10 rounded-none h-9"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-black/10">{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Button onClick={remove} variant="ghost" data-testid="adv-delete-btn" title="Delete inquiry" className="rounded-none text-[#EF4444] hover:text-[#EF4444] hover:bg-[#EF4444]/10">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
